import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import type { IconDefinition } from '@fortawesome/angular-fontawesome';
import { faCircleQuestion } from '@fortawesome/pro-light-svg-icons';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { GenericBadgeComponent } from '@h20-shared/generic-badge/generic-badge.component';
import type {
    KpiCardAlignment,
    KpiCardBadge,
    KpiCardLabelEmphasis,
    KpiCardLink,
    KpiCardStatus,
    KpiCardValueEmphasis,
    KpiCardValueSuffix,
} from './pulse-kpi-card.types';

/** Shown in place of a value the card does not have. */
export const KPI_CARD_NO_VALUE = '—';

@Component({
    selector: 'pulse-kpi-card',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FaIconComponent, NgbTooltip, GenericBadgeComponent, RouterLink],
    templateUrl: './pulse-kpi-card.component.html',
    styleUrl: './pulse-kpi-card.component.scss',
    host: {
        class: 'pulse-kpi-card',
        '[class.pulse-kpi-card--interactive]': 'interactive()',
        '[attr.data-status]': 'status()',
        '[attr.data-value-emphasis]': 'valueEmphasis()',
        '[attr.data-alignment]': 'alignment()',
        '[attr.data-label-emphasis]': 'labelEmphasis()',
        '[attr.role]': "interactive() ? 'button' : null",
        '[attr.tabindex]': 'interactive() ? 0 : null',
        '[attr.aria-busy]': "loading() ? 'true' : null",
        '(click)': 'onActivate($event)',
        '(keydown.enter)': 'onActivate($event)',
        '(keydown.space)': 'onActivate($event)',
    },
})
export class PulseKpiCardComponent {
    readonly label = input.required<string>();
    readonly value = input<string | number | undefined>(undefined);
    readonly caption = input<string | undefined>(undefined);
    /** Text behind the question mark icon. The icon is hidden when empty. */
    readonly hint = input<string | undefined>(undefined);
    /** Light icon at the top right. */
    readonly icon = input<IconDefinition | undefined>(undefined);
    readonly badges = input<readonly KpiCardBadge[]>([]);
    readonly status = input<KpiCardStatus>('neutral');
    /** Whether the value is still on its way, so the card draws a skeleton instead. */
    readonly loading = input(false);
    /**
     * Extra text drawn after the card's main value.
     * `word` is the joining word and `value` is the second number, so a card showing `3`
     * with `{ word: 'of', value: '10' }` reads `3 of 10`.
     */
    readonly valueSuffix = input<KpiCardValueSuffix | undefined>(undefined);
    readonly link = input<KpiCardLink | undefined>(undefined);
    /** How big the main value is drawn */
    readonly valueEmphasis = input<KpiCardValueEmphasis>('standard');
    /** Which side the value, the second number and the caption sit on */
    readonly alignment = input<KpiCardAlignment>('left');
    /** How the label is drawn. `emphasized` makes it heavier at the same size. */
    readonly labelEmphasis = input<KpiCardLabelEmphasis>('default');
    /** Whether the whole card acts as a button and emits `activate`. */
    readonly interactive = input(false);
    readonly activate = output<void>();

    protected readonly faCircleQuestion = faCircleQuestion;

    protected readonly shownValue = computed(() => {
        const value = this.value();
        return value === undefined || value === '' ? KPI_CARD_NO_VALUE : `${value}`;
    });

    protected readonly shownSuffix = computed(() =>
        this.shownValue() === KPI_CARD_NO_VALUE ? undefined : this.valueSuffix()
    );

    protected onActivate(event: Event): void {
        if (!this.interactive()) {
            return;
        }
        if (event instanceof KeyboardEvent) {
            // A focused help button or link inside the card handles its own keys.
            if (event.target !== event.currentTarget) {
                return;
            }
            event.preventDefault();
        }
        this.activate.emit();
    }
}
